import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { toast } from "sonner";

const SPORTS = ["Soccer", "Basketball", "Football", "Baseball", "Volleyball", "Hockey", "Lacrosse", "Sports Performance"];
const SPORT_IMG = {
  Soccer: "https://images.unsplash.com/photo-1560272564-c83b66b1ad12?w=400&q=70",
  Basketball: "https://images.unsplash.com/photo-1612768875331-0447b960fa40?w=400&q=70",
  Football: "https://images.unsplash.com/photo-1488474739786-757973c2dff6?w=400&q=70",
  Baseball: "https://images.unsplash.com/photo-1529768167801-9173d94c2a42?w=400&q=70",
  Volleyball: "https://images.unsplash.com/photo-1686753767715-37cb0c34212c?w=400&q=70",
  Hockey: "https://images.unsplash.com/photo-1516226415502-d6624544376b?w=400&q=70",
  Lacrosse: "https://images.unsplash.com/photo-1746826092606-ffbcac530b89?w=400&q=70",
  "Sports Performance": "https://images.unsplash.com/photo-1526506118085-60ce8714f8c5?w=400&q=70",
};

const MODELS = [
  { id: "anthropic/claude-sonnet-4-5-20250929", provider: "anthropic", name: "claude-sonnet-4-5-20250929", label: "Claude Sonnet 4.5 · deep coaching" },
  { id: "openai/gpt-5.2", provider: "openai", name: "gpt-5.2", label: "GPT-5.2 · balanced" },
  { id: "gemini/gemini-3-flash-preview", provider: "gemini", name: "gemini-3-flash-preview", label: "Gemini 3 Flash · fast" },
];

function Stepper({ step }) {
  const steps = ["Sport", "Video", "You in frame", "Analyze"];
  return (
    <div className="flex items-center gap-4 mb-8">
      {steps.map((s, i) => (
        <React.Fragment key={s}>
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-full grid place-items-center font-display font-bold step-dot ${i <= step ? "bg-red-600 text-white" : "bg-neutral-800 text-neutral-400"}`}>{i + 1}</div>
            <span className={`font-display uppercase tracking-widest text-sm ${i <= step ? "text-white" : "text-neutral-500"}`}>{s}</span>
          </div>
          {i < steps.length - 1 && <div className={`h-px flex-1 ${i < step ? "bg-red-600" : "bg-neutral-800"}`} />}
        </React.Fragment>
      ))}
    </div>
  );
}

export default function Upload() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [sport, setSport] = useState("");
  const [goal, setGoal] = useState("");
  const [file, setFile] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [videoId, setVideoId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [idMode, setIdMode] = useState("jersey"); // jersey | highlight | both
  const [jersey, setJersey] = useState("");
  const [color, setColor] = useState("");
  const [markedFrame, setMarkedFrame] = useState(null);
  const [model, setModel] = useState(MODELS[0].id);
  const [submitting, setSubmitting] = useState(false);
  const [samplingSport, setSamplingSport] = useState(null);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const boxRef = useRef({ start: null, end: null, drawing: false });
  const [boxDisplay, setBoxDisplay] = useState(null);

  const onFile = (f) => {
    if (!f) return;
    if (!f.type.startsWith("video/")) return toast.error("Please choose a video file");
    if (f.size > 300 * 1024 * 1024) return toast.error("Video must be under 300MB");
    setFile(f);
    setVideoUrl(URL.createObjectURL(f));
  };

  const uploadVideo = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/videos/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setVideoId(r.data.video_id);
      setStep(2);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const captureFrame = () => {
    const v = videoRef.current;
    const c = canvasRef.current;
    if (!v || !c) return null;
    c.width = v.videoWidth || 640; c.height = v.videoHeight || 360;
    const ctx = c.getContext("2d");
    ctx.drawImage(v, 0, 0, c.width, c.height);
    return { ctx, c };
  };

  const saveHighlight = () => {
    const grab = captureFrame();
    if (!grab) return toast.error("Play the video and pause on a frame first");
    const { ctx, c } = grab;
    if (boxRef.current.start && boxRef.current.end) {
      const s = boxRef.current.start, e = boxRef.current.end;
      const displayEl = videoRef.current.getBoundingClientRect();
      const sx = c.width / displayEl.width, sy = c.height / displayEl.height;
      const x = Math.min(s.x, e.x) * sx, y = Math.min(s.y, e.y) * sy;
      const w = Math.abs(e.x - s.x) * sx, h = Math.abs(e.y - s.y) * sy;
      ctx.strokeStyle = "#ef4444"; ctx.lineWidth = 4;
      ctx.strokeRect(x, y, w, h);
    }
    const dataUrl = c.toDataURL("image/jpeg", 0.8);
    setMarkedFrame(dataUrl.split(",")[1]);
    toast.success("Frame captured");
  };

  const onBoxDown = (e) => {
    const r = videoRef.current.getBoundingClientRect();
    boxRef.current = { start: { x: e.clientX - r.left, y: e.clientY - r.top }, end: null, drawing: true };
    setBoxDisplay(null);
  };
  const onBoxMove = (e) => {
    if (!boxRef.current.drawing) return;
    const r = videoRef.current.getBoundingClientRect();
    boxRef.current.end = { x: e.clientX - r.left, y: e.clientY - r.top };
    setBoxDisplay({ ...boxRef.current });
  };
  const onBoxUp = () => { boxRef.current.drawing = false; };

  const submit = async () => {
    if (!videoId || !sport) return;
    const [provider, ...rest] = model.split("/");
    setSubmitting(true);
    try {
      const r = await api.post("/analyses", {
        video_id: videoId,
        sport,
        performance_goal: sport === "Sports Performance" ? goal : null,
        player_jersey: jersey || null,
        player_color: color || null,
        player_highlight_frame: markedFrame,
        model_provider: provider,
        model_name: rest.join("/"),
      });
      toast.success("Analysis started");
      nav(`/analysis/${r.data.analysis_id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start analysis");
    } finally { setSubmitting(false); }
  };

  return (
    <div>
      <div className="rs-underline mb-3" />
      <h1 className="font-display-heavy text-4xl mb-6">NEW ANALYSIS</h1>
      <Stepper step={step} />

      {step === 0 && (
        <div>
          <h2 className="font-display-heavy text-2xl mb-4">PICK YOUR SPORT</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {SPORTS.map((s) => (
              <button data-testid={`sport-pick-${s.toLowerCase().replace(/ /g, "-")}`} key={s} onClick={() => setSport(s)}
                className={`text-left rounded-md overflow-hidden border transition-colors ${sport === s ? "border-red-600 ring-2 ring-red-600" : "border-neutral-800 hover:border-neutral-600"}`}>
                <div className="aspect-[4/3]"><img alt={s} src={SPORT_IMG[s]} className="w-full h-full object-cover opacity-80" /></div>
                <div className="p-3 font-display-heavy text-lg bg-neutral-900">{s}</div>
              </button>
            ))}
          </div>
          {sport && (
            <div className="mt-6 max-w-lg space-y-2">
              <Label htmlFor="goal" className="text-base">
                What are you working on? <span className="text-neutral-500 font-normal">{sport === "Sports Performance" ? "(required)" : "(optional)"}</span>
              </Label>
              <Textarea data-testid="goal-textarea" id="goal"
                placeholder={sport === "Sports Performance"
                  ? "e.g., improve deadlift form, sprint agility, single-leg balance…"
                  : `e.g., ${sport === "Basketball" ? "fix elbow drift on my jump shot" : sport === "Soccer" ? "cleaner first touch, sharper crosses" : "tighten technique on my main move"}…`}
                value={goal} onChange={(e) => setGoal(e.target.value)} className="min-h-24 bg-neutral-900 border-neutral-700" />
              <p className="text-xs text-neutral-500">Your coach will weight feedback toward this goal.</p>
            </div>
          )}
          <div className="mt-8 flex flex-wrap gap-3">
            <Button data-testid="upload-step-next-0" disabled={!sport || (sport === "Sports Performance" && !goal.trim())} onClick={() => setStep(1)}
              className="h-12 px-6 bg-red-600 hover:bg-red-700 font-semibold">Continue →</Button>
            <Button data-testid="try-sample-btn" disabled={!sport || !!samplingSport} onClick={async () => {
              const [provider, ...rest] = model.split("/");
              setSamplingSport(sport);
              try {
                const r = await api.post("/analyses/from-sample", {
                  sport,
                  performance_goal: goal.trim() || null,
                  model_provider: provider,
                  model_name: rest.join("/"),
                });
                toast.success("Demo analysis started — free");
                nav(`/analysis/${r.data.analysis_id}`);
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Could not start demo");
                setSamplingSport(null);
              }
            }} variant="outline" className="h-12 px-6 border-neutral-700 text-white hover:bg-neutral-900">
              {samplingSport ? "Loading demo…" : "Try a sample clip (free)"}
            </Button>
          </div>
          {sport && (
            <p className="text-xs text-neutral-500 mt-3">Sample clips don't count against your monthly quota.</p>
          )}
        </div>
      )}

      {step === 1 && (
        <div className="max-w-2xl">
          <h2 className="font-display-heavy text-2xl mb-4">UPLOAD YOUR CLIP</h2>
          <p className="text-neutral-400 text-sm mb-4">Up to 90 seconds, 300MB max. Private to you.</p>
          {!file ? (
            <label data-testid="video-file-dropzone" className="block border-2 border-dashed border-neutral-700 rounded-md p-10 text-center cursor-pointer hover:border-red-600 transition-colors">
              <input data-testid="video-file-input" type="file" accept="video/*" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} />
              <div className="font-display-heavy text-2xl">DROP A VIDEO OR CLICK TO CHOOSE</div>
              <div className="text-neutral-400 text-sm mt-2">MP4, MOV, or WebM — up to 300MB</div>
            </label>
          ) : (
            <div className="rounded-md overflow-hidden border border-neutral-800 bg-black">
              <video src={videoUrl} controls className="w-full aspect-video" />
              <div className="p-3 flex items-center justify-between bg-neutral-900">
                <div className="text-sm text-neutral-400 truncate">{file.name} · {(file.size / 1024 / 1024).toFixed(1)} MB</div>
                <Button data-testid="video-clear-btn" variant="ghost" onClick={() => { setFile(null); setVideoUrl(null); }} className="text-white hover:bg-neutral-800">Change</Button>
              </div>
            </div>
          )}
          <div className="mt-6 flex gap-3">
            <Button data-testid="upload-step-back-1" variant="outline" onClick={() => setStep(0)} className="h-12 px-6 border-neutral-700 text-white hover:bg-neutral-900">← Back</Button>
            <Button data-testid="upload-step-next-1" disabled={!file || uploading} onClick={uploadVideo} className="h-12 px-6 bg-red-600 hover:bg-red-700 font-semibold">
              {uploading ? "Uploading…" : "Upload & Continue →"}
            </Button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div>
          <h2 className="font-display-heavy text-2xl mb-4">WHICH PLAYER ARE YOU?</h2>
          <p className="text-neutral-400 mb-4">Help our AI coach focus on the right person on the field.</p>
          <RadioGroup value={idMode} onValueChange={setIdMode} className="grid md:grid-cols-3 gap-3 mb-6">
            {[["jersey", "Jersey # / Color"], ["highlight", "Highlight on frame"], ["both", "Both"]].map(([v, l]) => (
              <label key={v} className={`flex items-center gap-3 p-4 rounded-md border cursor-pointer ${idMode === v ? "border-red-600 bg-neutral-900" : "border-neutral-800"}`}>
                <RadioGroupItem data-testid={`id-mode-${v}`} value={v} />
                <span className="font-display text-lg uppercase">{l}</span>
              </label>
            ))}
          </RadioGroup>

          {(idMode === "jersey" || idMode === "both") && (
            <div className="grid md:grid-cols-2 gap-4 mb-6 max-w-2xl">
              <div className="space-y-2">
                <Label htmlFor="jersey" className="text-base">Jersey number</Label>
                <Input data-testid="jersey-input" id="jersey" placeholder="e.g., 23" value={jersey} onChange={(e) => setJersey(e.target.value)} className="h-12 bg-neutral-900 border-neutral-700" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="color" className="text-base">Jersey color / kit</Label>
                <Input data-testid="color-input" id="color" placeholder="e.g., red shirt, black shorts" value={color} onChange={(e) => setColor(e.target.value)} className="h-12 bg-neutral-900 border-neutral-700" />
              </div>
            </div>
          )}

          {(idMode === "highlight" || idMode === "both") && (
            <div className="max-w-2xl">
              <p className="text-sm text-neutral-400 mb-3">Pause on a frame that shows you clearly, then drag a box around yourself.</p>
              <div className="relative rounded-md overflow-hidden border border-neutral-800 bg-black select-none">
                <video ref={videoRef} src={videoUrl} controls className="w-full aspect-video block"
                  onMouseDown={onBoxDown} onMouseMove={onBoxMove} onMouseUp={onBoxUp} onMouseLeave={onBoxUp} />
                {boxDisplay?.start && boxDisplay?.end && (
                  <div className="absolute border-2 border-red-500 pointer-events-none"
                    style={{
                      left: Math.min(boxDisplay.start.x, boxDisplay.end.x),
                      top: Math.min(boxDisplay.start.y, boxDisplay.end.y),
                      width: Math.abs(boxDisplay.end.x - boxDisplay.start.x),
                      height: Math.abs(boxDisplay.end.y - boxDisplay.start.y),
                    }} />
                )}
              </div>
              <canvas ref={canvasRef} className="hidden" />
              <div className="mt-3 flex gap-2">
                <Button data-testid="capture-frame-btn" onClick={saveHighlight} variant="outline" className="h-11 border-neutral-700 text-white hover:bg-neutral-900">Capture highlighted frame</Button>
                {markedFrame && <span className="text-green-500 text-sm self-center">✓ Frame saved</span>}
              </div>
            </div>
          )}

          <div className="mt-8 max-w-lg">
            <Label className="text-base">AI Coach model</Label>
            <Select value={model} onValueChange={setModel}>
              <SelectTrigger data-testid="model-select" className="h-12 mt-2 bg-neutral-900 border-neutral-700"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MODELS.map((m) => <SelectItem key={m.id} value={m.id}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="mt-8 flex gap-3">
            <Button data-testid="upload-step-back-2" variant="outline" onClick={() => setStep(1)} className="h-12 px-6 border-neutral-700 text-white hover:bg-neutral-900">← Back</Button>
            <Button data-testid="upload-step-submit" disabled={submitting} onClick={submit} className="h-12 px-6 bg-red-600 hover:bg-red-700 font-semibold">
              {submitting ? "Starting…" : "Start Analysis →"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
